// Ordered from highest to lowest privilege.
export const ACCESS_LEVEL_HIERARCHY = [
  "superuser",
  "admin",
  "manager",
  "staff",
  "paralegal",
  "viewer",
];

export const ACCESS_LEVELS = {
  SUPERUSER: "superuser",
  ADMIN: "admin",
  MANAGER: "manager",
  STAFF: "staff",
  PARALEGAL: "paralegal",
  VIEWER: "viewer",
};

export const LEGACY_ACCESS_LEVEL_ALIASES = {
  paid: ACCESS_LEVELS.ADMIN,
  member: ACCESS_LEVELS.STAFF,
};

const ORGANIZATION_ACCESS_LEVEL_FIELDS = [
  "organizationAccessLevel",
  "organization_access_level",
  "orgAccessLevel",
  "org_access_level",
];

const PRO_SE_MARKERS = ["pro se", "pro-se", "self represented", "self-represented"];

// Display labels for dropdowns and tables.
export const ACCESS_LEVEL_OPTIONS = [
  { value: ACCESS_LEVELS.ADMIN, label: "Admin" },
  { value: ACCESS_LEVELS.MANAGER, label: "Manager" },
  { value: ACCESS_LEVELS.STAFF, label: "Staff" },
  { value: ACCESS_LEVELS.PARALEGAL, label: "Paralegal" },
  { value: ACCESS_LEVELS.VIEWER, label: "Viewer" },
];

// Includes superuser for internal-ops selects where all tiers should be available.
export const ACCESS_LEVEL_OPTIONS_ALL = [
  { value: ACCESS_LEVELS.SUPERUSER, label: "Superuser" },
  ...ACCESS_LEVEL_OPTIONS,
];

export const normalizeAccessLevel = (value) =>
  typeof value === "string"
    ? LEGACY_ACCESS_LEVEL_ALIASES[value.trim().toLowerCase()] || value.trim().toLowerCase()
    : "";

// Returns true if userLevel is at least as privileged as requiredLevel.
export const hasMinimumAccessLevel = (userLevel, requiredLevel) => {
  const userIndex = ACCESS_LEVEL_HIERARCHY.indexOf(normalizeAccessLevel(userLevel));
  const requiredIndex = ACCESS_LEVEL_HIERARCHY.indexOf(normalizeAccessLevel(requiredLevel));
  if (userIndex === -1 || requiredIndex === -1) {
    return false;
  }
  return userIndex <= requiredIndex;
};

export const isSuperuserAccessLevel = (accessLevel) =>
  normalizeAccessLevel(accessLevel) === ACCESS_LEVELS.SUPERUSER;

export const isAdminAccessLevel = (accessLevel) =>
  hasMinimumAccessLevel(accessLevel, ACCESS_LEVELS.ADMIN);

export const isManagerAccessLevel = (accessLevel) =>
  normalizeAccessLevel(accessLevel) === ACCESS_LEVELS.MANAGER;

export const isStaffAccessLevel = (accessLevel) =>
  normalizeAccessLevel(accessLevel) === ACCESS_LEVELS.STAFF;

export const isParalegalAccessLevel = (accessLevel) =>
  normalizeAccessLevel(accessLevel) === ACCESS_LEVELS.PARALEGAL;

export const isViewerAccessLevel = (accessLevel) =>
  normalizeAccessLevel(accessLevel) === ACCESS_LEVELS.VIEWER;

const readNormalizedAccessLevelFromFields = (candidateUser, fields = []) =>
  fields
    .map((field) => normalizeAccessLevel(candidateUser?.[field]))
    .find(Boolean) || "";

const readExplicitUserTypeSignals = (candidateUser) =>
  [
    candidateUser?.accountType,
    candidateUser?.account_type,
    candidateUser?.userType,
    candidateUser?.user_type,
    candidateUser?.organizationType,
    candidateUser?.organization_type,
    candidateUser?.pricingTier,
    candidateUser?.pricing_tier,
    candidateUser?.planName,
    candidateUser?.plan_name,
    candidateUser?.subscriptionTier,
    candidateUser?.subscription_tier,
  ]
    .filter((value) => typeof value === "string")
    .map((value) => value.trim().toLowerCase());

export const isExplicitProSeUser = (candidateUser) =>
  readExplicitUserTypeSignals(candidateUser).some((value) =>
    PRO_SE_MARKERS.some((marker) => value.includes(marker)),
  );

export const resolveUserAccessLevel = (candidateUser, { appId } = {}) => {
  const directLevel = normalizeAccessLevel(
    candidateUser?.accessLevel || candidateUser?.access_level || candidateUser?.role,
  );
  if (directLevel) {
    return directLevel;
  }

  const nestedAccessRecords = [
    candidateUser?.userAppAccess,
    candidateUser?.data?.userAppAccess,
    ...(Array.isArray(candidateUser?.data?.allUserAppAccesses)
      ? candidateUser.data.allUserAppAccesses
      : []),
    ...(Array.isArray(candidateUser?.allUserAppAccesses)
      ? candidateUser.allUserAppAccesses
      : []),
  ]
    .filter(Boolean)
    .filter((record) => String(record?.appId ?? '').trim().toLowerCase() !== 'global')
    .filter((record) =>
      appId === undefined || appId === null
        ? true
        : String(record?.appId ?? '') === String(appId),
    );

  const normalizedLevels = nestedAccessRecords
    .map((record) => normalizeAccessLevel(record?.accessLevel || record?.access_level))
    .filter(Boolean)
    .sort(
      (left, right) =>
        ACCESS_LEVEL_HIERARCHY.indexOf(left) - ACCESS_LEVEL_HIERARCHY.indexOf(right),
    );

  return normalizedLevels[0] || "";
};

export const resolveOrganizationAccessLevel = (candidateUser, options = {}) => {
  if (!candidateUser) {
    return "";
  }

  // An explicit Pro Se account marker is a hard boundary for organization
  // administration, even when legacy/product access records contain a higher
  // app-level role. Evaluate it before the platform superuser shortcut so a
  // stale entitlement cannot bypass the account-type restriction.
  if (isExplicitProSeUser(candidateUser)) {
    return "";
  }

  /*
   * Superuser is a platform-wide role, not an org-scoped one. Every other
   * branch below answers "what is this person's role *inside their
   * organization*", which returns "" for anyone with no organizationId - so a
   * superuser with no org row was silently demoted to no authority at all and
   * lost Billing, Manage Subscriptions and Manage Organization everywhere.
   * Unlike the "free" billing tier, "superuser" is a genuine ladder rung, so
   * short-circuiting here cannot leak paid access to an unentitled account.
   */
  if (isSuperuserAccessLevel(resolveUserAccessLevel(candidateUser, options))) {
    return ACCESS_LEVELS.SUPERUSER;
  }

  const directOrganizationLevel = readNormalizedAccessLevelFromFields(
    candidateUser,
    ORGANIZATION_ACCESS_LEVEL_FIELDS,
  );
  if (directOrganizationLevel) {
    return directOrganizationLevel;
  }

  if (candidateUser?.data) {
    const nestedOrganizationLevel = readNormalizedAccessLevelFromFields(
      candidateUser.data,
      ORGANIZATION_ACCESS_LEVEL_FIELDS,
    );
    if (nestedOrganizationLevel) {
      return nestedOrganizationLevel;
    }
  }

  if (!candidateUser?.organizationId && !candidateUser?.data?.organizationId) {
    return "";
  }

  return resolveUserAccessLevel(candidateUser, options);
};

export const userHasMinimumAccessLevel = (candidateUser, requiredLevel, options = {}) =>
  hasMinimumAccessLevel(resolveUserAccessLevel(candidateUser, options), requiredLevel);

export const userHasMinimumOrganizationAccessLevel = (candidateUser, requiredLevel, options = {}) =>
  hasMinimumAccessLevel(resolveOrganizationAccessLevel(candidateUser, options), requiredLevel);

export const canManageOrganizationWorkspace = (candidateUser) =>
  userHasMinimumOrganizationAccessLevel(candidateUser, ACCESS_LEVELS.ADMIN);

export const getAccessLevelLabel = (accessLevel) => {
  const normalized = normalizeAccessLevel(accessLevel);
  return (
    ACCESS_LEVEL_OPTIONS_ALL.find((option) => option.value === normalized)?.label ||
    (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Unknown")
  );
};
